/**
 * Screen 1f — A3 landscape composite interpretation log.
 *
 * The sheet is laid out at exactly 1587 × 1123 px, the pixel size of A3
 * landscape at 96 dpi (420 × 297 mm, ratio 1.414). `box-sizing: border-box`
 * on the sheet keeps the outer box exactly that size once padding is applied —
 * the fix the mockup itself needed before it would export cleanly.
 *
 * Curves arrive already normalised to 0–100 from the server, so the paper and
 * the screen cannot disagree about scaling.
 */

import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { Logo } from "../components/icons";
import { Button, Loading, Notice } from "../components/ui";
import { useAsync } from "../hooks";
import type { CpiPayload } from "../types";

const SHEET_W = 1587;
const SHEET_H = 1123;

export default function PrintCPI() {
  const { wellId } = useParams();
  const [search] = useSearchParams();

  const sheet = useAsync(
    (signal) =>
      api.get<CpiPayload>(
        `/api/exports/cpi/${wellId}/`,
        {
          run: search.get("run") ?? undefined,
          top: search.get("top") ?? undefined,
          base: search.get("base") ?? undefined,
          scale: search.get("scale") ?? 200,
        },
        signal,
      ),
    [wellId, search.toString()],
  );

  if (sheet.loading) return <Loading label="Laying out the CPI sheet…" />;
  if (sheet.error)
    return (
      <div style={{ padding: 24 }}>
        <Notice tone="error">{sheet.error}</Notice>
      </div>
    );
  if (!sheet.data) return null;

  const data = sheet.data;

  return (
    <div className="print-sheets" style={{ minHeight: "100%", background: "var(--app-bg)", padding: "20px 0 60px" }}>
      <div
        className="no-print"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          maxWidth: SHEET_W,
          margin: "0 auto 16px",
          padding: "0 8px",
        }}
      >
        <span style={{ font: "600 13px var(--font-sans)", color: "var(--ink)" }}>
          Print preview — A3 landscape
        </span>
        <span style={{ font: "400 11px var(--font-mono)", color: "var(--ink-6)" }}>
          {data.footer.page}
        </span>
        <span style={{ flex: 1 }} />
        <Button variant="ghost" onClick={() => history.back()}>
          Back
        </Button>
        <Button variant="primary" onClick={() => window.print()}>
          Print / save PDF
        </Button>
      </div>

      <CpiSheet data={data} />
    </div>
  );
}

/**
 * Satu lembar A3 mendatar, dengan geometri piksel tetap.
 *
 * Berdiri sendiri sebagai komponen karena lembar yang dicetak satu-satu dan
 * lembar dalam berkas berisi dua puluh sumur harus digambar oleh kode yang
 * sama. Dua salinan tata letak yang berbeda tipis adalah dua tafsiran yang
 * dipisahkan hanya oleh cara memintanya, dan selisihnya baru ketahuan setelah
 * kertasnya tercetak.
 *
 * `break-after` menaruh tiap lembar pada halamannya sendiri. Tanpanya sebuah
 * berkas dua puluh sumur tercetak sebagai satu gulungan yang terpotong di
 * tengah kurva.
 */
export function CpiSheet({ data, breakAfter = false }: { data: CpiPayload; breakAfter?: boolean }) {
  return (
    <div
      className="cpi-sheet"
      style={{
        boxSizing: "border-box",
        width: SHEET_W,
        height: SHEET_H,
        margin: "0 auto",
        breakAfter: breakAfter ? "page" : "auto",
        background: "#fff",
        color: "#23211d",
        padding: "34px 38px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        borderRadius: 2,
        boxShadow: "0 8px 40px rgba(0,0,0,.3)",
        fontFamily: '"IBM Plex Sans", sans-serif',
      }}
    >
      {/* Title block */}
      <header
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          borderBottom: "2px solid #23211d",
          paddingBottom: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
          <Logo size={26} />
          {/* The same panel colour as the correlation screen. Print sheet and
              screen are read side by side; if the same well carries
              berbeda di keduanya, warnanya berhenti berarti. */}
          {data.panel_color && (
            <span
              aria-hidden
              style={{
                width: 5,
                alignSelf: "stretch",
                background: data.panel_color,
                borderRadius: 2,
              }}
            />
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ font: '600 19px "IBM Plex Sans", sans-serif', letterSpacing: "-.01em" }}>
              {data.title}
            </span>
            <span style={{ font: '400 12px "IBM Plex Mono", monospace', color: "#7c766b" }}>
              {data.subtitle}
            </span>
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(150px, auto))",
            gap: "2px 26px",
          }}
        >
          {data.header.map((entry) => (
            <div
              key={entry.k}
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 12,
                borderBottom: "1px dotted #c8c1b4",
                paddingBottom: 2,
              }}
            >
              <span style={{ font: '400 11px "IBM Plex Sans", sans-serif', color: "#7c766b" }}>
                {entry.k}
              </span>
              <span style={{ font: '600 11.5px "IBM Plex Mono", monospace' }}>{entry.v}</span>
            </div>
          ))}
        </div>
      </header>

      <div style={{ flex: 1, display: "flex", gap: 16, minHeight: 0 }}>
        {/* Composite log */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "stretch", height: 56, border: "1px solid #23211d", borderBottom: "none" }}>
            <div
              style={{
                width: 66,
                flex: "none",
                borderRight: "1px solid #23211d",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                paddingBottom: 5,
              }}
            >
              <span style={{ font: '600 10.5px "IBM Plex Mono", monospace' }}>MD (m)</span>
            </div>
            {data.tracks.map((track) => (
              <div
                key={track.title}
                style={{
                  flex: track.flex,
                  minWidth: 0,
                  borderRight: "1px solid #ddd8ce",
                  padding: "5px 8px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                  <span style={{ font: '600 11px "IBM Plex Sans", sans-serif' }}>{track.title}</span>
                  <span style={{ font: '400 9.5px "IBM Plex Mono", monospace', color: "#a09889" }}>
                    {track.units}
                  </span>
                </div>
                {track.curves.map((curve) => (
                  <div key={curve.name} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span
                      style={{
                        width: 14,
                        height: 0,
                        flex: "none",
                        borderTop: `2px solid ${printColor(curve.name)}`,
                      }}
                    />
                    <span style={{ font: '400 10px "IBM Plex Mono", monospace', color: "#5c564b" }}>
                      {curve.name}
                    </span>
                    <span style={{ flex: 1 }} />
                    <span style={{ font: '400 9.5px "IBM Plex Mono", monospace', color: "#a09889" }}>
                      {curve.range}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "stretch",
              minHeight: 0,
              border: "1px solid #23211d",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: 66,
                flex: "none",
                borderRight: "1px solid #23211d",
                display: "flex",
                flexDirection: "column",
                background: "#faf9f5",
              }}
            >
              {data.depth_ticks.map((depth, index) => (
                <div
                  key={index}
                  style={{
                    flex: 1,
                    borderTop: "1px solid #ddd8ce",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-end",
                    padding: "3px 8px 0 0",
                  }}
                >
                  <span style={{ font: '500 11px "IBM Plex Mono", monospace', color: "#4c463c" }}>
                    {depth.toFixed(0)}
                  </span>
                </div>
              ))}
            </div>

            {data.tracks.map((track) => (
              <PrintTrack key={track.title} track={track} interval={data.interval} zones={data.zones} />
            ))}
          </div>
        </div>

        {/* Right column */}
        <div style={{ width: 398, flex: "none", display: "flex", flexDirection: "column", gap: 14 }}>
          <section style={{ border: "1px solid #23211d" }}>
            <div
              style={{
                padding: "7px 11px",
                background: "#23211d",
                color: "#fff",
                font: '600 10.5px "IBM Plex Mono", monospace',
                letterSpacing: ".08em",
              }}
            >
              SUMATIF RESERVOIR (m)
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1.6fr .8fr .8fr .8fr .9fr .8fr" }}>
              {data.summary.head.map((head, index) => (
                <div
                  key={head}
                  style={{
                    padding: "6px 8px",
                    background: "#f4f2ed",
                    borderBottom: "1px solid #23211d",
                    font: '600 10px "IBM Plex Mono", monospace',
                    color: "#4c463c",
                    textAlign: index === 0 ? "left" : "right",
                  }}
                >
                  {head}
                </div>
              ))}
              {data.summary.rows.map((row) => (
                <PrintRow key={row.z} row={row} />
              ))}
            </div>
          </section>

          <section style={{ border: "1px solid #23211d" }}>
            <div
              style={{
                padding: "7px 11px",
                background: "#23211d",
                color: "#fff",
                font: '600 10.5px "IBM Plex Mono", monospace',
                letterSpacing: ".08em",
              }}
            >
              PARAMETER
            </div>
            <div
              style={{
                padding: "9px 11px",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "5px 18px",
              }}
            >
              {data.parameters.map((entry) => (
                <div
                  key={entry.k}
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 10,
                    borderBottom: "1px dotted #ddd8ce",
                  }}
                >
                  <span style={{ font: '400 10.5px "IBM Plex Sans", sans-serif', color: "#5c564b" }}>
                    {entry.k}
                  </span>
                  <span style={{ font: '600 10.5px "IBM Plex Mono", monospace' }}>{entry.v}</span>
                </div>
              ))}
            </div>
          </section>

          <section style={{ border: "1px solid #23211d" }}>
            <div
              style={{
                padding: "7px 11px",
                background: "#23211d",
                color: "#fff",
                font: '600 10.5px "IBM Plex Mono", monospace',
                letterSpacing: ".08em",
              }}
            >
              LITHOLOGY LEGEND
            </div>
            <div style={{ padding: "9px 11px", display: "flex", flexWrap: "wrap", gap: "7px 16px" }}>
              {data.lithology_legend.map((entry) => (
                <div key={entry.name} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <span
                    style={{ width: 16, height: 11, background: entry.fill, border: "1px solid #23211d" }}
                  />
                  <span style={{ font: '400 10.5px "IBM Plex Sans", sans-serif', color: "#4c463c" }}>
                    {entry.name}
                  </span>
                </div>
              ))}
            </div>
          </section>

          <div style={{ marginTop: "auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <SignatureBox
              caption="Prepared by"
              name={data.provenance.prepared_by.name}
              role={data.provenance.prepared_by.role}
              at={data.provenance.prepared_by.at}
            />
            <SignatureBox
              caption="Approved by"
              name={data.provenance.approved_by.name}
              role={data.provenance.approved_by.role}
              at={data.provenance.approved_by.at}
            />
          </div>
        </div>
      </div>

      <footer
        style={{
          display: "flex",
          alignItems: "center",
          gap: 20,
          borderTop: "1px solid #23211d",
          paddingTop: 9,
          font: '400 10px "IBM Plex Mono", monospace',
          color: "#7c766b",
        }}
      >
        <span>File: {data.footer.file}</span>
        <span>CRS: {data.footer.crs}</span>
        <span>Unit: {data.footer.units}</span>
        <span>Set: {data.provenance.parameter_set}</span>
        <span style={{ flex: 1 }} />
        <span>{data.footer.page}</span>
      </footer>
    </div>
  );
}

/** Print palette — the light "Core Paper" curve colours. */
function printColor(mnemonic: string): string {
  const palette: Record<string, string> = {
    GR: "#4a8c3a",
    SP: "#6b5bb0",
    RT: "#a9722a",
    RXO: "#c49a63",
    RHOB: "#b0475c",
    NPHI: "#1f6f7a",
    PHIE: "#1f6f7a",
    SW: "#4a5f96",
  };
  return palette[mnemonic] ?? "#23211d";
}

function PrintRow({
  row,
}: {
  row: { z: string; g: string; nr: string; np: string; p: string; s: string; bold: boolean };
}) {
  const cell = (value: string, align: "left" | "right" = "right") => (
    <div
      style={{
        padding: "6px 8px",
        borderBottom: "1px solid #eee9df",
        font: `${row.bold ? 600 : 400} 11px "IBM Plex Mono", monospace`,
        textAlign: align,
      }}
    >
      {value}
    </div>
  );
  return (
    <>
      {cell(row.z, "left")}
      {cell(row.g)}
      {cell(row.nr)}
      {cell(row.np)}
      {cell(row.p)}
      {cell(row.s)}
    </>
  );
}

function PrintTrack({
  track,
  interval,
  zones,
}: {
  track: CpiPayload["tracks"][number];
  interval: [number, number];
  zones: CpiPayload["zones"];
}) {
  const [top, base] = interval;
  const span = base - top || 1;

  return (
    <div
      style={{
        position: "relative",
        flex: track.flex,
        minWidth: 0,
        borderRight: "1px solid #ddd8ce",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "repeating-linear-gradient(to right, rgba(0,0,0,.08) 0 1px, transparent 1px 20%), repeating-linear-gradient(to bottom, rgba(0,0,0,.06) 0 1px, transparent 1px 34px)",
        }}
      />

      {zones.map((zone) => {
        const y = ((zone.top - top) / span) * 100;
        const h = ((zone.base - zone.top) / span) * 100;
        if (y > 100 || y + h < 0) return null;
        return (
          <div
            key={zone.name}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: `${y}%`,
              height: `${h}%`,
              background: "rgba(31,111,122,.07)",
              borderTop: "1px solid #1f6f7a",
              borderBottom: "1px solid #1f6f7a",
            }}
          />
        );
      })}

      {track.lithology &&
        track.blocks.map((block, index) => {
          const y = ((block.top - top) / span) * 100;
          const h = ((block.base - block.top) / span) * 100;
          if (y > 100 || y + h < 0) return null;
          return (
            <div
              key={index}
              title={block.name}
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: `${y}%`,
                height: `${h}%`,
                background: block.fill,
                borderBottom: "1px solid rgba(0,0,0,.15)",
              }}
            />
          );
        })}

      <svg
        viewBox="0 0 100 1000"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        aria-label={track.title}
      >
        {track.curves.map((curve) => (
          <path
            key={curve.name}
            d={normalisedPath(curve.md, curve.normalised, top, base)}
            fill="none"
            stroke={printColor(curve.name)}
            strokeWidth={1.1}
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
    </div>
  );
}

/** Server-normalised values (0–100) plus depths → an SVG path, broken at nulls. */
function normalisedPath(
  depths: number[],
  values: (number | null)[],
  top: number,
  base: number,
): string {
  const span = base - top || 1;
  const parts: string[] = [];
  let open = false;
  for (let i = 0; i < depths.length; i++) {
    const value = values[i];
    if (value === null || !Number.isFinite(value)) {
      open = false;
      continue;
    }
    const y = ((depths[i] - top) / span) * 1000;
    parts.push(`${open ? "L" : "M"}${value.toFixed(2)},${y.toFixed(1)}`);
    open = true;
  }
  return parts.join(" ");
}

function SignatureBox({
  caption,
  name,
  role,
  at,
}: {
  caption: string;
  name: string;
  role: string;
  at: string | null;
}) {
  return (
    <div
      style={{
        height: 70,
        border: "1px solid #23211d",
        padding: "6px 9px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <span style={{ font: '400 9.5px "IBM Plex Mono", monospace', color: "#7c766b" }}>{caption}</span>
      {at && (
        <span style={{ font: '400 9px "IBM Plex Mono", monospace', color: "#a09889" }}>
          {new Date(at).toLocaleDateString("en-GB")}
        </span>
      )}
      <span style={{ flex: 1 }} />
      <span style={{ borderTop: "1px solid #c8c1b4", paddingTop: 3, display: "flex", gap: 8 }}>
        <span style={{ font: '600 10px "IBM Plex Sans", sans-serif', color: "#23211d" }}>{name}</span>
        <span style={{ font: '400 10px "IBM Plex Sans", sans-serif', color: "#7c766b" }}>{role}</span>
      </span>
    </div>
  );
}
